// ============================================
// Post-Processing Effects - Index
// ============================================

// Export all effects
export { BloomEffect, BloomQuality } from './BloomEffect';
export { ChromaticAberrationEffect } from './ChromaticAberrationEffect';
export { ColorGradingEffect } from './ColorGradingEffect';
export { LensDistortionEffect } from './LensDistortionEffect';
export { LensFlareEffect } from './LensFlareEffect';
export { VignetteEffect } from './VignetteEffect';
export { ChannelMixerEffect } from './ChannelMixerEffect';
export { FilmGrainEffect } from './FilmGrainEffect';
export { LiftGammaGainEffect } from './LiftGammaGainEffect';
export { MotionBlurEffect, MotionBlurQuality } from './MotionBlurEffect';
export { WhiteBalanceEffect } from './WhiteBalanceEffect';
export { DebugUVEffect } from './DebugUVEffect';
export { AquaEffect, AquaOverlayMode } from './AquaEffect';
export { TonemapEffect, TonemapMode } from './TonemapEffect';
export { GodRaysEffect } from './GodRaysEffect';
export { KuwaharaEffect } from './KuwaharaEffect';
export { CRTEffect } from './CRTEffect';
export { HeatHazeEffect } from './HeatHazeEffect';
export { GlitchEffect } from './GlitchEffect';
export { HalftoneEffect } from './HalftoneEffect';
export { PixelArtEffect, PixelArtPalette } from './PixelArtEffect';
export { UnderwaterEffect } from './UnderwaterEffect';
export { OutlineEffect } from './OutlineEffect';
export { ZoomBlurEffect } from './ZoomBlurEffect';

// Effect types for registration
import { BloomEffect } from './BloomEffect';
import { ChromaticAberrationEffect } from './ChromaticAberrationEffect';
import { ColorGradingEffect } from './ColorGradingEffect';
import { LensDistortionEffect } from './LensDistortionEffect';
import { LensFlareEffect } from './LensFlareEffect';
import { VignetteEffect } from './VignetteEffect';
import { ChannelMixerEffect } from './ChannelMixerEffect';
import { FilmGrainEffect } from './FilmGrainEffect';
import { LiftGammaGainEffect } from './LiftGammaGainEffect';
import { MotionBlurEffect } from './MotionBlurEffect';
import { WhiteBalanceEffect } from './WhiteBalanceEffect';
import { DebugUVEffect } from './DebugUVEffect';
import { AquaEffect } from './AquaEffect';
import { TonemapEffect } from './TonemapEffect';
import { GodRaysEffect } from './GodRaysEffect';
import { KuwaharaEffect } from './KuwaharaEffect';
import { CRTEffect } from './CRTEffect';
import { HeatHazeEffect } from './HeatHazeEffect';
import { GlitchEffect } from './GlitchEffect';
import { HalftoneEffect } from './HalftoneEffect';
import { PixelArtEffect } from './PixelArtEffect';
import { UnderwaterEffect } from './UnderwaterEffect';
import { OutlineEffect } from './OutlineEffect';
import { ZoomBlurEffect } from './ZoomBlurEffect';
import type { PostProcessEffect } from '../PostProcessStack';

/**
 * Effect registry entry
 */
export interface EffectRegistryEntry {
  type: string;
  displayName: string;
  category: string;
  create: () => PostProcessEffect;
}

/**
 * Built-in effect registry
 */
export const builtinEffects: EffectRegistryEntry[] = [
  // Lens & Distortion
  {
    type: 'bloom',
    displayName: 'Bloom',
    category: 'Lens',
    create: () => new BloomEffect(),
  },
  {
    type: 'chromaticAberration',
    displayName: 'Chromatic Aberration',
    category: 'Lens',
    create: () => new ChromaticAberrationEffect(),
  },
  {
    type: 'lensDistortion',
    displayName: 'Lens Distortion',
    category: 'Lens',
    create: () => new LensDistortionEffect(),
  },
  {
    type: 'lensFlare',
    displayName: 'Lens Flare',
    category: 'Lens',
    create: () => new LensFlareEffect(),
  },
  {
    type: 'vignette',
    displayName: 'Vignette',
    category: 'Lens',
    create: () => new VignetteEffect(),
  },
  
  // Light
  {
    type: 'godRays',
    displayName: 'God Rays',
    category: 'Light',
    create: () => new GodRaysEffect(),
  },

  // Color
  {
    type: 'tonemap',
    displayName: 'Tonemap',
    category: 'Color',
    create: () => new TonemapEffect(),
  },
  {
    type: 'colorGrading',
    displayName: 'Color Grading',
    category: 'Color',
    create: () => new ColorGradingEffect(),
  },
  {
    type: 'whiteBalance',
    displayName: 'White Balance',
    category: 'Color',
    create: () => new WhiteBalanceEffect(),
  },
  {
    type: 'channelMixer',
    displayName: 'Channel Mixer',
    category: 'Color',
    create: () => new ChannelMixerEffect(),
  },
  {
    type: 'liftGammaGain',
    displayName: 'Lift Gamma Gain',
    category: 'Color',
    create: () => new LiftGammaGainEffect(),
  },
  
  // Film
  {
    type: 'filmGrain',
    displayName: 'Film Grain',
    category: 'Film',
    create: () => new FilmGrainEffect(),
  },
  {
    type: 'motionBlur',
    displayName: 'Motion Blur',
    category: 'Film',
    create: () => new MotionBlurEffect(),
  },
  {
    type: 'zoomBlur',
    displayName: 'Radial Blur',
    category: 'Film',
    create: () => new ZoomBlurEffect(),
  },

  // Retro
  {
    type: 'crt',
    displayName: 'CRT',
    category: 'Retro',
    create: () => new CRTEffect(),
  },
  {
    type: 'glitch',
    displayName: 'Glitch',
    category: 'Retro',
    create: () => new GlitchEffect(),
  },
  {
    type: 'pixelArt',
    displayName: 'Pixel Art',
    category: 'Retro',
    create: () => new PixelArtEffect(),
  },

  // Distortion
  {
    type: 'heatHaze',
    displayName: 'Heat Haze',
    category: 'Distortion',
    create: () => new HeatHazeEffect(),
  },
  {
    type: 'underwater',
    displayName: 'Underwater',
    category: 'Distortion',
    create: () => new UnderwaterEffect(),
  },

  // Artistic
  {
    type: 'aqua',
    displayName: 'Watercolor',
    category: 'Artistic',
    create: () => new AquaEffect(),
  },
  {
    type: 'kuwahara',
    displayName: 'Oil Painting',
    category: 'Artistic',
    create: () => new KuwaharaEffect(),
  },
  {
    type: 'halftone',
    displayName: 'Halftone',
    category: 'Artistic',
    create: () => new HalftoneEffect(),
  },
  {
    type: 'outline',
    displayName: 'Toon Outline',
    category: 'Artistic',
    create: () => new OutlineEffect(),
  },
  
  // Debug
  {
    type: 'debugUV',
    displayName: 'Debug UV',
    category: 'Debug',
    create: () => new DebugUVEffect(),
  },
];

/**
 * Create an effect by type name
 */
export function createEffect(type: string): PostProcessEffect | null {
  const entry = builtinEffects.find(e => e.type === type);
  return entry ? entry.create() : null;
}

